// HeaderHint — the header row's hover explanation. One surface for every chip
// (keep-awake, engine), styled like the plan badge's hover card rather than the
// app's black tooltip, so the row reads as one family.
//
// Radix Tooltip underneath, for the parts that are easy to get wrong by hand:
// hover-intent delay, focus/keyboard opening, Escape, collision-aware placement,
// and HOVERABLE content — the pointer can travel into the card, so an action
// inside it (e.g. "Learn more") is clickable without a click to open it first.
// Portalled through usePortalContainer so it lands inside the extension's
// shadow root exactly as the rest of the header does.
import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import hs from './header-status.module.scss';

export type HeaderHintTone = 'ok' | 'warn' | 'bad' | 'neutral';

const RAIL: Record<HeaderHintTone, string> = {
  ok: hs.hintRailOk,
  warn: hs.hintRailWarn,
  bad: hs.hintRailBad,
  neutral: hs.hintRailNeutral,
};

interface HeaderHintProps {
  /** The element the hint explains. Must accept a ref (rendered asChild). */
  children: React.ReactElement;
  /** Optional bold first line. */
  title?: React.ReactNode;
  /** The explanation itself — one or two plain sentences. */
  body: React.ReactNode;
  /** A quiet trailing line or action (e.g. "Click to manage…", a Learn-more link). */
  footer?: React.ReactNode;
  /** Colours the card's rail to match the chip's state. */
  tone?: HeaderHintTone;
}

export const HeaderHint: React.FC<HeaderHintProps> = ({ children, title, body, footer, tone = 'neutral' }) => {
  const container = usePortalContainer();
  return (
    <TooltipPrimitive.Provider delayDuration={250} skipDelayDuration={150}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={container}>
          <TooltipPrimitive.Content side="bottom" align="center" sideOffset={8} collisionPadding={8} className={hs.hint}>
            <span className={`${hs.hoverRail} ${RAIL[tone]}`} />
            <div className={hs.hoverText}>
              {title && <div className={hs.hoverFeature}>{title}</div>}
              <div className={title ? hs.hintBody : hs.hoverFeature}>{body}</div>
              {footer && <div className={hs.hoverStatus}>{footer}</div>}
            </div>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

export default HeaderHint;
